import React, { useState } from 'react';

const Section = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-gray-700/50 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-700/80 transition-colors"
      >
        <span className="text-gray-400 text-xs">{open ? '\u25BC' : '\u25B6'}</span>
        <span className="text-white font-semibold text-sm">{title}</span>
      </button>
      {open && <div className="px-4 pb-4 text-sm text-gray-300 space-y-3">{children}</div>}
    </div>
  );
};

const Tip = ({ children }) => (
  <div className="px-3 py-2 bg-green-600/10 border border-green-600/30 rounded text-xs text-green-400">
    {children}
  </div>
);

const Warning = ({ children }) => (
  <div className="px-3 py-2 bg-amber-400/10 border border-amber-400/30 rounded text-xs text-amber-300">
    {children}
  </div>
);

const GuidePage = () => {
  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-2">Portal Guide</h1>
      <p className="text-sm text-gray-400 mb-6">How each action works.</p>

      <div className="space-y-3">
        <Section title="Managing Jobs" defaultOpen>
          <div className="space-y-2">
            <div>
              <span className="text-white font-medium">Edit Job</span> (pencil icon on a job card)
              <p className="text-gray-400 mt-0.5">
                Opens the job form. Changes here are <span className="text-amber-300">permanent</span> — they update the
                job record and are written back to the Google Sheet automatically. This affects the job everywhere
                it appears (Booked Work, Routes, etc.).
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Delete Job</span> (red button inside Edit Job form)
              <p className="text-gray-400 mt-0.5">
                Removes the job <span className="text-green-400">from this route only</span>. The job itself stays
                intact — it returns to the unassigned pool on the Booked Work page and can be re-assigned to any route.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Assign to Route</span> (dropdown on Booked Work page or Unassigned Jobs panel)
              <p className="text-gray-400 mt-0.5">
                Assigns the job to the route. It appears on Day 1 of the selected route.
                The job details are unchanged — only the route assignment is created.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">+ Job</span> (button on day header)
              <p className="text-gray-400 mt-0.5">
                Creates a brand new job and assigns it to this day. Jobs created here won't appear in the
                Google Sheet — add them to the sheet manually if needed.
              </p>
            </div>
            <Tip>Deleting a job from a route never deletes the job itself. You can always re-assign it later.</Tip>
          </div>
        </Section>

        <Section title="Route Operations">
          <div className="space-y-2">
            <div>
              <span className="text-white font-medium">Drag &amp; Drop Jobs</span>
              <p className="text-gray-400 mt-0.5">
                Drag the grip handle (&#9776;) on any unpinned job to reorder within a day or move to a different day.
                Travel times become stale after moves — click "Recalculate Travel" to update them.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Drag Days</span> (grip on day header)
              <p className="text-gray-400 mt-0.5">
                Drag a day header to swap its position with another day. All jobs on both days swap day numbers.
                Days with pinned jobs cannot be dragged — unpin first.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Pin / Unpin</span>
              <p className="text-gray-400 mt-0.5">
                Pinning a job locks it in place. Pinned jobs are <span className="text-orange-300">never moved</span> by
                drag-and-drop, Cancel Day, or auto-sort. You can set an arrival time and pin reason.
                Unpin to allow the job to be moved again.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Cancel Day</span>
              <p className="text-gray-400 mt-0.5">
                Pushes all <em>non-pinned</em> jobs on this day (and all subsequent days) forward by one day.
                Nothing is deleted — this is for weather days or schedule shifts. Pinned jobs stay put.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Extend Day</span>
              <p className="text-gray-400 mt-0.5">
                Overrides the default 8-hour day limit. The hours display changes from "X / 8.0h" to the new limit.
                This only affects the hour tracking display — it doesn't prevent adding more jobs.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Move to Route</span>
              <p className="text-gray-400 mt-0.5">
                Moves all jobs from a day to a different route. Travel rows are deleted (recalculated on the target route).
                Remaining days on the source route are renumbered to close the gap.
                Days with pinned jobs cannot be moved — unpin first.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Edit Route</span> (pencil icon next to route name)
              <p className="text-gray-400 mt-0.5">
                Change the route name, corridor, start date, or notes. If the route has an applicator assigned,
                you <span className="text-red-400">cannot</span> change the start date to overlap with another route
                for the same applicator — the save will be blocked with a message showing the conflicting route.
                If the route has pinned jobs, you'll see a warning that their calendar dates will shift.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Push Route</span> (button in route header)
              <p className="text-gray-400 mt-0.5">
                Moves the entire route to start the following Monday. If that Monday is a statutory holiday,
                it starts Tuesday (or the next available weekday). If this causes an overlap with another route
                for the same applicator, that route is <span className="text-amber-300">automatically pushed too</span> —
                the cascade continues until there are no conflicts. Pinned jobs move with the route but are
                flagged for <span className="text-amber-300">review</span> since their calendar dates change.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Pin Review</span> (amber "Review" badge on pinned jobs)
              <p className="text-gray-400 mt-0.5">
                After a route push, pinned jobs show an amber glow and a "Review" badge. Click "Review" to
                open the pin form, confirm or update the arrival time and notes, then click "Confirm Pin" to
                clear the flag. This ensures you verify fixed-time appointments after dates shift.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Route Status</span> (buttons in route header)
              <p className="text-gray-400 mt-0.5">
                <span className="text-gray-300">Draft → Active → Completed</span> is the normal flow.
                Marking a route <span className="text-red-300">Cancelled</span> returns all jobs to the unassigned pool
                and deletes travel rows. The route itself is kept for records.
              </p>
            </div>
            <Warning>Cancelling a route removes all job assignments. The jobs go back to Booked Work as unassigned.</Warning>
          </div>
        </Section>

        <Section title="Travel">
          <div className="space-y-2">
            <div>
              <span className="text-white font-medium">Calculate / Recalculate Travel</span>
              <p className="text-gray-400 mt-0.5">
                Computes drive times between consecutive jobs using Google Maps, plus start-of-day
                and end-of-day travel. Travel rows are regenerated, but any Home/Hotel toggles or
                custom addresses you've set are preserved.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Home / Hotel Toggle</span>
              <p className="text-gray-400 mt-0.5">
                Each start-of-day and end-of-day travel row has a pill toggle.
                <span className="text-green-400"> Home</span> = calculated drive to/from the applicator's home base.
                <span className="text-blue-300"> Hotel</span> = flat 25-minute estimate.
                Defaults: the route's first day starts from Home, its last day ends at Home, and every day in between is Hotel.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Inline Address Edit</span>
              <p className="text-gray-400 mt-0.5">
                Click the description text on any start/end-of-day travel row to type a custom address.
                It pre-fills with the applicator's home base from their profile. Custom addresses persist through recalculations.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Stale Travel</span>
              <p className="text-gray-400 mt-0.5">
                After dragging jobs or days, affected travel rows are marked "STALE" in amber.
                Click "Recalculate Travel" to update them with fresh Google Maps data.
              </p>
            </div>
            <Tip>You can click any travel time value to manually override it (e.g., if you know a shortcut).</Tip>
          </div>
        </Section>

        <Section title="Time &amp; Payroll (Time-Zone Policy)">
          <div className="space-y-2">
            <div>
              <span className="text-white font-medium">Start Time / Stop Time</span> (buttons on each day header)
              <p className="text-gray-400 mt-0.5">
                <span className="text-gray-300">Starting the day</span> — enter your start time and confirm your
                location/time zone. The portal pre-fills the zone from your route's first stop; correct it if you
                started somewhere else.<br />
                <span className="text-gray-300">Crossing a zone</span> — on a crossing day, confirm your end location
                when prompted.<br />
                <span className="text-gray-300">Ending the day</span> — enter your stop time and confirm you're ending
                where the route says.<br />
                <span className="text-gray-300">Entering hours later</span> — enter the date you actually worked and
                your location on that day, not today's. The portal defaults the zone to that day's route location;
                leave it unless you know it's wrong.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">What you'll see</span>
              <p className="text-gray-400 mt-0.5">
                Every time shows its zone (e.g. <span className="text-gray-300">08:00 PDT</span> — "PDT/PST" is Pacific,
                "MDT/MST" is Mountain). The day header switches from an <span className="text-gray-300">estimated</span>{' '}
                badge to an <span className="text-green-400">actual</span> badge once both clock points exist. A day that
                touched more than one zone is marked <span className="text-blue-300">crossed&nbsp;tz</span>.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">The Other segment</span> (+ Travel → Other)
              <p className="text-gray-400 mt-0.5">
                Use <span className="text-gray-300">Transit</span> for a driving segment (looked up via Google Maps) and{' '}
                <span className="text-gray-300">Other</span> for a non-driving block — standby, repositioning — as a
                duration plus a note. Other segments are skipped by Recalculate Travel.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Time &amp; Payroll page</span> (admin)
              <p className="text-gray-400 mt-0.5">
                Pick an employee (or all) and a date range, then Export CSV for payroll. The Open Days tab lists any
                entry still missing a stop time so nothing drops from a pay period.
              </p>
            </div>
          </div>
        </Section>

        <Section title="Sync (Google Sheets)">
          <div className="space-y-2">
            <div>
              <span className="text-white font-medium">Sync Button</span> (Booked Work page)
              <p className="text-gray-400 mt-0.5">
                Pulls all rows from the Google Sheet and updates your jobs. You can freely insert, delete,
                and reorder rows in the sheet without breaking route assignments.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Write-Back</span>
              <p className="text-gray-400 mt-0.5">
                When you edit a job in the portal, changes are automatically written back to the Google Sheet.
                If the write-back fails for any reason, the portal still has the correct data.
              </p>
            </div>
            <Warning>
              Jobs created via "+ Job" only exist in the portal. They won't appear in the Google Sheet unless
              you add them manually.
            </Warning>
          </div>
        </Section>

        <Section title="Profile &amp; Applicators">
          <div className="space-y-2">
            <div>
              <span className="text-white font-medium">My Profile</span> (bottom of sidebar)
              <p className="text-gray-400 mt-0.5">
                Set your name, home base address, and default hotel travel time. Your home base is used for
                travel calculations — it's the starting point on the route's first day and the destination on its last day.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Assign Applicator to Route</span> (dropdown in route header)
              <p className="text-gray-400 mt-0.5">
                Sets who is running the route. This determines which home base address is used for travel calculations.
              </p>
            </div>
            <div>
              <span className="text-white font-medium">Change Password</span> (on Profile page)
              <p className="text-gray-400 mt-0.5">
                Update your login password. Minimum 6 characters.
              </p>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default GuidePage;
